import { Injectable } from '@angular/core';
import { ComponentStore } from '@ngrx/component-store';
import { GenesysCloudUser } from 'common/store/genesys-cloud/users/models/user.model';
import { UserApiService, UserSearchResponse } from 'common/store/genesys-cloud/users/services/user-api.service';
import { QueueApiService } from 'common/store/genesys-cloud/queues/services/queue-api.service';
import { forkJoin, Observable, Subject } from 'rxjs';
import { distinctUntilChanged, filter, first, map, switchMap, tap } from 'rxjs/operators';
import { GenesysCloudQueue } from 'common/store/genesys-cloud/queues/models/genesys-cloud-queue.model';
import { QueueSearchResponse } from 'common/store/genesys-cloud/queues/models/queue-search-response.model';

interface TransferPanelSearchState {
  searchTerm: string;
  users: TransferPanelPagedEntity<GenesysCloudUser>;
  queues: TransferPanelPagedEntity<GenesysCloudQueue>;
  loading: boolean;
}

interface TransferPanelPagedEntity<T> {
  entities: T[],
  actualPage: number;
  totalPages: number;
  totalCount: number;
}

export const initialState: TransferPanelSearchState = {
  searchTerm: '',
  loading: false,
  users: {
    entities: [],
    actualPage: 0,
    totalPages: 0,
    totalCount: 0
  },
  queues: {
    entities: [],
    actualPage: 0,
    totalPages: 0,
    totalCount: 0
  }
};

@Injectable()
export class GenesysTransferPanelSearchStore extends ComponentStore<TransferPanelSearchState> {
  static readonly pageSize = 100;

  readonly users$ = this.select(state => state.users.entities);
  readonly queues$ = this.select(state => state.queues.entities);
  readonly loading$ = this.select(state => state.loading);

  readonly canLoadMoreUsers$ = this.select(state => state.users.actualPage < state.users.totalPages);
  readonly canLoadMoreQueues$ = this.select(state => state.queues.actualPage < state.queues.totalPages);

  private readonly loadNextPageUsersSubject = new Subject<void>();
  private readonly loadNextPageQueuesSubject = new Subject<void>();

  search(searchTerm: string): void {
    this.patchState({ searchTerm });
  }

  loadAdditionalUsers(): void {
    this.loadNextPageUsersSubject.next();
  }

  loadAdditionalQueues(): void {
    this.loadNextPageQueuesSubject.next();
  }

  clear(): void {
    this.patchState({ searchTerm: '' });
  }

  constructor(private readonly userApiService: UserApiService,
    private readonly queueApiService: QueueApiService) {
    super(initialState);
    const searchTerm$ = this.select(state => state.searchTerm);

    const newSearchSource$ = searchTerm$.pipe(
      distinctUntilChanged(),
      map(searchTerm => ({ searchTerm, pageNumber: 1 })),
      tap(() => this.patchState(() => ({ loading: true })))
    );

    newSearchSource$
      .pipe(
        tap(() => this.patchState((state) => ({ ...state, loading: true }))),
        switchMap(({ searchTerm, pageNumber }) =>
          forkJoin([
            this.getUsersBySearchTerm$(searchTerm, pageNumber),
            this.queueApiService.searchQueues$(searchTerm, GenesysTransferPanelSearchStore.pageSize, pageNumber)
          ])
        ),
        tap(() => this.patchState((state) => ({ ...state, loading: false })))
      )
      .subscribe(([a, b]) => {
        this.handleUserSearchResponse(a);
        this.handleQueueSearchResponse(b);
    });

    const nextUserPageSource$ = this.loadNextPageUsersSubject.pipe(
      switchMap(() => this.state$.pipe(first())),
      filter(state => state.users.actualPage < state.users.totalPages),
      map((state) => ({ searchTerm: state.searchTerm, pageNumber: state.users.actualPage + 1 }))
    );

    const nextQueuePageSource$ = this.loadNextPageQueuesSubject.pipe(
      switchMap(() => this.state$.pipe(first())),
      filter(state => state.queues.actualPage < state.queues.totalPages),
      map((state) => ({ searchTerm: state.searchTerm, pageNumber: state.queues.actualPage + 1 }))
    );

    nextUserPageSource$.pipe(
      switchMap(({ searchTerm, pageNumber }) => this.getUsersBySearchTerm$(searchTerm, pageNumber))
    ).subscribe(response => this.handleUserSearchResponse(response));

    nextQueuePageSource$.pipe(
      switchMap(({ searchTerm, pageNumber }) => this.queueApiService.searchQueues$(searchTerm, GenesysTransferPanelSearchStore.pageSize, pageNumber))
    ).subscribe(response => this.handleQueueSearchResponse(response));
  }

  private getUsersBySearchTerm$(searchTerm: string, pageNumber: number): Observable<UserSearchResponse> {
    return searchTerm?.length > 0
      ? this.userApiService.searchUsers$(searchTerm, GenesysTransferPanelSearchStore.pageSize, pageNumber)
      : this.userApiService.getUsers$(GenesysTransferPanelSearchStore.pageSize, pageNumber);
  }

  private handleUserSearchResponse({ results, pageNumber, pageCount, total }: UserSearchResponse): void {
    results = results || [];

    this.patchState(state => ({
      loading: false,
      users: {
        ...state.users,
        entities: pageNumber === 1 ? results : [...state.users.entities, ...results],
        actualPage: pageNumber,
        totalPages: pageCount,
        totalCount: total
      }
    }));
  }

  private handleQueueSearchResponse({ entities, pageNumber, pageCount, total }: QueueSearchResponse): void {
    entities = entities || [];

    this.patchState(state => ({
      loading: false,
      queues: {
        ...state.queues,
        entities: pageNumber === 1 ? entities : [...state.queues.entities, ...entities],
        actualPage: pageNumber,
        totalPages: pageCount,
        totalCount: total
      }
    }));
  }
}
